import { isEmail } from 'validator'
import { create, enforce, only, test } from 'vest'

import {
  getClosestElement,
  getElementChild,
  getElementChildren,
  getElements,
} from '../Utility/Dom'
import { IS_BAD, IS_LOADING, IS_OK } from '../Utility/State'
import { ERROR_CODE_COUNT, ERROR_CODE_EMAIL, ERROR_CODE_EMPTY, ERROR_CODE_INTEGER } from '../Validation/ErrorCodes'
import { isValidJson } from '../Validation/Json'

enforce.extend({
  isEmail,
  isSingleFile: (value) => {
    return value.name !== ''
  }
})

type FormValidationObject = { field: string; rules: ValidationRules }
type ValidationMessage = { code: string; message: string }
type ValidationRule = { max?: number; message: string; min?: number; }
type ValidationRules = { [key: string]: ValidationRule }

export default class Form {
  private forms: [] | HTMLFormElement[] = []
  private registeredElements: Set<HTMLElement>
  private validationSuites: { formId: string; formValidationObjects: FormValidationObject[]; formValidationSuiteInstance: ReturnType<typeof create>; }[]

  constructor() {
    this.forms = Array.from(getElements('form')) as HTMLFormElement[]
    this.registeredElements = new Set()
    this.validationSuites = []
  }

  public init(): void {
    this.initScrollToSection()
    this.initScrollToSectionButtons()

    this.forms.forEach((form: HTMLFormElement): void => {
      const formId: string = form.getAttribute('data-id')
      if (!formId) return

      const formElements = getElementChildren(form, 'form-element')
      const formValidationObjects: FormValidationObject[] = []

      formElements.forEach((formElement: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void => {
        this.registerFormElement(form, formId, formElement, formValidationObjects)
      })

      form.setAttribute('novalidate', 'novalidate')
      this.initFormValidationSuite(formId, formValidationObjects)

      form.addEventListener('submit', (event: SubmitEvent): void => {
        this.handleFormSubmit(form, formId, event)
      })
    })
  }

  private addError(container: HTMLElement, formElements, errors): void {
    const errorContainer = getElementChild(container, 'form-error') as HTMLElement
    if (errorContainer === null) return

    errorContainer.innerText = ''
    errors.forEach((error): void => {
      errorContainer.innerText += error + '\n'
    })

    formElements.forEach((formElement: HTMLElement): void => formElement.setAttribute('aria-invalid', 'true'))
  }

  private attachFieldListeners(
    form: HTMLFormElement,
    formId: string,
    formElement: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ) {
    const formElementType = formElement.type
    let fieldChanged = false

    if (
      formElementType === 'radio' ||
      formElementType === 'checkbox' ||
      formElementType === 'select-one'
    ) {
      formElement.addEventListener('change', (): void => {
        this.handleFormElementInput(form, formId, formElement)
      })
    } else {
      formElement.addEventListener('change', (): void => {
        fieldChanged = true
      })

      formElement.addEventListener('input', (): void => {
        this.removeErrorsForFormElement(form, formElement.name)
      })

      formElement.addEventListener('blur', (): void => {
        if (fieldChanged === false) {
          return
        }
        this.handleFormElementInput(form, formId, formElement)
      })
    }
  }

  private createValidationSuite(formValidationObjects: FormValidationObject[]) {
    return create((data = {}, currentField): void => {
      only(currentField)

      formValidationObjects.forEach((formValidationObject): void => {
        const fieldElement = document.querySelector(`[name="${formValidationObject.field}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        if (!fieldElement || fieldElement.disabled) {
          return
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'notEmpty')) {
          test(formValidationObject.field, formValidationObject.rules.notEmpty.message, (): void => {
            const value = data[formValidationObject.field]
            if (typeof value === 'boolean') {
              enforce(value).isTruthy()
            } else if (Array.isArray(value)) {
              enforce(value.length).greaterThan(0)
            } else {
              enforce(value).isNotBlank()
            }
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'count')) {
          const countRule = formValidationObject.rules.count
          test(formValidationObject.field, countRule.message, (): void => {
            const value = data[formValidationObject.field]
            const count = Array.isArray(value) ? value.length : (value ? 1 : 0)
            enforce(count)
              .greaterThanOrEquals(countRule.min ?? 0)
              .lessThanOrEquals(countRule.max ?? Number.MAX_SAFE_INTEGER)
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'noFile')) {
          const submittedFile = document.querySelector('[name="' + formValidationObject.field + '[submittedFile][resourcePointer]"]')

          if (submittedFile !== null) {
            return
          }

          test(formValidationObject.field, formValidationObject.rules.noFile.message, (): void => {
            enforce(data[formValidationObject.field]).isSingleFile()
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'email')) {
          if (data[formValidationObject.field] && data[formValidationObject.field] !== '') {
            test(formValidationObject.field, formValidationObject.rules.email.message, (): void => {
              enforce(data[formValidationObject.field]).isEmail()
            })
          }
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'integer')) {
          if (data[formValidationObject.field] && data[formValidationObject.field] !== '') {
            test(formValidationObject.field, formValidationObject.rules.integer.message, (): void => {
              enforce(data[formValidationObject.field]).matches(/^\d+$/)
            })
          }
        }
      })
    })
  }

  private getFormData(form): Record<string, unknown> {
    const formElements = getElementChildren(form, 'form-element')
    const data: Record<string, unknown> = {}

    formElements.forEach((element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void => {
      if (!element.disabled && element.name) {
        if (element.type === 'checkbox') {
          if (element.name.endsWith('[]')) {
            const values = Array.isArray(data[element.name]) ? data[element.name] as string[] : []
            if ((element as HTMLInputElement).checked) {
              values.push(element.value)
            }
            data[element.name] = values
          } else {
            data[element.name] = (element as HTMLInputElement).checked
          }
        } else if (element.type === 'radio') {
          if ((element as HTMLInputElement).checked) {
            data[element.name] = element.value
          }
        } else if (element.type === 'file') {
          data[element.name] = (element as HTMLInputElement).files[0] || { name: '' }
        } else {
          data[element.name] = element.value
        }
      }
    })

    return data
  }

  private getFormDataAndRemoveFormErrors(form): Record<string, unknown> {
    const formElements = getElementChildren(form, 'form-element')

    formElements.forEach((formElement) => {
      this.removeErrorsForFormElement(form, formElement.name)
    })

    return this.getFormData(form)
  }

  private getFormValidationSuiteByFormId(formIdentifier) {
    return this.validationSuites.find((validationSuite): boolean => {
      return validationSuite.formId === formIdentifier
    })
  }

  private handleFormElementInput(form, formId, formElement): void {
    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)
    if (typeof formValidationSuiteObject === 'undefined') return

    const formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance
    this.removeErrorsForFormElement(form, formElement.name)
    const fieldValidationObject = this.getFormData(form)
    formValidationSuite.reset()

    const result = formValidationSuite.run(
      fieldValidationObject,
      formElement.name
    )

    if (result.hasErrors()) {
      this.showErrors(form, result.getErrors())
    } else {
      this.markFormElementValid(form, formElement.name)
    }
  }

  private handleFormSubmit(form: HTMLFormElement, formId: string, event: SubmitEvent): void {
    // Validate on forward navigation (multi-step "next" buttons) as well as the final submit, so
    // each step is checked client-side before advancing. Skip backward navigation (formnovalidate)
    // and programmatic submits that carry no submitter.
    if (!event.submitter || event.submitter.hasAttribute('formnovalidate')) {
      return
    }

    const submitButton = getElementChild(form, 'form-submit') as HTMLButtonElement | null

    // Prevent double submissions
    if (submitButton?.disabled) {
      event.preventDefault()
      return
    }

    const formElements = getElementChildren(form, 'form-element') as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]
    const activeFieldNames = Array.from(formElements)
      .filter((el) => !el.disabled && el.name)
      .map((el) => el.name)

    formElements.forEach((formElement): void => {
      if (formElement.disabled) return

      this.handleFormElementInput(form, formId, formElement)
    })

    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)

    if (typeof formValidationSuiteObject === 'undefined') {
      return
    }

    const formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance.run(
      this.getFormDataAndRemoveFormErrors(form)
    )

    const errors = Object.fromEntries(
      Object.entries(formValidationSuite.getErrors())
        .filter(([field]) => activeFieldNames.includes(field))
    )

    const errorFieldNames = Object.keys(errors)

    if (errorFieldNames.length > 0) {
      event.preventDefault()
      this.showErrors(form, errors, true)

      activeFieldNames
        .filter((fieldName) => !errorFieldNames.includes(fieldName))
        .forEach((fieldName) => this.markFormElementValid(form, fieldName))
    } else {
      activeFieldNames.forEach((fieldName) => {
        this.removeErrorsForFormElement(form, fieldName)
        this.markFormElementValid(form, fieldName)
      })

      // Disable submit button AFTER form submission starts (with minimal delay)
      // to prevent double clicks while still sending the button value
      if (submitButton) {
        submitButton.classList.add(IS_LOADING)
        setTimeout((): void => {
          submitButton.disabled = true
        }, 10)
      }
    }
  }

  private initFormValidationSuite(formIdentifier: string, formValidationObjects: FormValidationObject[]): void {
    this.validationSuites.push({
      formId: formIdentifier,
      formValidationObjects: formValidationObjects,
      formValidationSuiteInstance: this.createValidationSuite(formValidationObjects)
    })
  }

  private initScrollToSection(): void {
    const scrollTarget = window.sessionStorage.getItem('formScrollToSection')
    if (!scrollTarget) return

    window.sessionStorage.removeItem('formScrollToSection')

    const targetElement =
      document.querySelector<HTMLElement>(`[data-identifier="${scrollTarget}"]`) ||
      document.getElementById(scrollTarget)

    if (targetElement) {
      setTimeout((): void => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }

  private initScrollToSectionButtons(): void {
    const scrollToButtons = document.querySelectorAll<HTMLButtonElement>('[data-scroll-to]')

    scrollToButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        const scrollTarget = button.dataset.scrollTo
        if (scrollTarget) {
          window.sessionStorage.setItem('formScrollToSection', scrollTarget)
        }
      })
    })
  }

  private markFormElementValid(form, formElementName: string): void {
    const formElements = form.querySelectorAll('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    const container = getClosestElement(formElements[0], 'form-ctr')
    if (!container) return

    container.classList.add(IS_OK)

    const successContainer = getElementChild(container, 'form-success') as HTMLElement | null
    if (successContainer) {
      successContainer.innerText = successContainer.dataset.message || ''
    }
  }

  private registerFormElement(
    form: HTMLFormElement,
    formId: string,
    formElement: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    formValidationObjects: FormValidationObject[]
  ) {
    if (this.registeredElements.has(formElement)) {
      return
    }

    this.registeredElements.add(formElement)

    const formGroup = getClosestElement(formElement, 'form-group') as HTMLElement | null
    const formElementType: string = formElement.type
    const validationRules: ValidationRules = {}

    if (formGroup && isValidJson(formGroup.dataset.msg) === true) {
      const errorMessages: [] = JSON.parse(formGroup.dataset.msg)
      const emptyError: undefined | ValidationMessage = errorMessages.filter((errorMessage: { code: number | string }): boolean => String(errorMessage.code) === ERROR_CODE_EMPTY)[0]
      const emailError: undefined | ValidationMessage = errorMessages.filter((errorMessage: { code: number | string }): boolean => String(errorMessage.code) === ERROR_CODE_EMAIL)[0]
      const integerError: undefined | ValidationMessage = errorMessages.filter((errorMessage: { code: number | string }): boolean => String(errorMessage.code) === ERROR_CODE_INTEGER)[0]
      const countError: undefined | ValidationMessage = errorMessages.filter((errorMessage: { code: number | string }): boolean => String(errorMessage.code) === ERROR_CODE_COUNT)[0]

      if (emptyError && formElementType !== 'file') {
        validationRules.notEmpty = {
          message: emptyError.message
        }
      }

      if (emptyError && formElementType === 'file') {
        validationRules.noFile = {
          message: emptyError.message
        }
      }

      if (emailError) {
        validationRules.email = {
          message: emailError.message
        }
      }

      if (integerError) {
        validationRules.integer = {
          message: integerError.message
        }
      }

      const minCount = formGroup.dataset.minCount
      const maxCount = formGroup.dataset.maxCount

      if (minCount || maxCount) {
        validationRules.count = {
          max: maxCount ? parseInt(maxCount, 10) : Number.MAX_SAFE_INTEGER,
          message: countError ? countError.message : '',
          min: minCount ? parseInt(minCount, 10) : 0
        }
      }

      const noValidationMessagesProvided: boolean = Object.keys(validationRules).length === 0 &&
        (formElement.required === true || formElement.getAttribute('data-required') === 'true')

      if (noValidationMessagesProvided === true) {
        if (formElementType !== 'file') {
          validationRules.notEmpty = {message: ''}
        } else {
          validationRules.noFile = {message: ''}
        }
      }
    }

    const alreadyRegistered = formValidationObjects.some((object): boolean => object.field === formElement.name)
    if (alreadyRegistered === false) {
      formValidationObjects.push({
        field: formElement.name,
        rules: validationRules
      })
    }

    this.attachFieldListeners(form, formId, formElement)
  }

  private removeErrorsForFormElement(form, formElementName: string): void {
    const formElements = form.querySelectorAll('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    formElements.forEach((formElement: HTMLElement): void => formElement.removeAttribute('aria-invalid'))

    const container = getClosestElement(formElements[0], 'form-ctr')
    if (!container) return

    container.classList.remove(IS_BAD, IS_OK)

    const errorContainer = getElementChild(container, 'form-error')
    if (!errorContainer) return
    errorContainer.innerText = ''

    const successContainer = getElementChild(container, 'form-success') as HTMLElement | null
    if (successContainer) {
      successContainer.innerText = ''
    }
  }

  private showErrors(form: HTMLFormElement, errorsByFormField: Record<string, string[]>, focusFirstError = false): void {
    for (const [formElementName, errorMessages] of Object.entries(errorsByFormField)) {
      this.showErrorsForFormElement(form, formElementName, errorMessages)
    }

    // Only move focus on submit. Doing it during per-field blur validation would
    // re-focus the field the user is trying to leave, creating a keyboard trap.
    if (focusFirstError === false) return

    const firstErrorElementName = Object.entries(errorsByFormField)[0][0]
    const firstErrorElement = form.querySelector<HTMLElement>('[data-js="form-element"][name="' + firstErrorElementName + '"]')
    if (firstErrorElement === null) return

    const scrollTarget = getClosestElement(firstErrorElement, 'form-ctr') ?? firstErrorElement
    const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0
    const top = scrollTarget.getBoundingClientRect().top + window.scrollY - offset

    window.scrollTo({ top: Math.max(top, 0) })
    firstErrorElement.focus({ preventScroll: true })
  }

  private showErrorsForFormElement(form: HTMLFormElement, formElementName, errorMessages): void {
    const formElements = form.querySelectorAll('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    const container = getClosestElement(formElements[0] as HTMLElement, 'form-ctr')
    if (!container) return

    container.classList.remove(IS_OK)
    container.classList.add(IS_BAD)

    this.addError(container, formElements, errorMessages)
  }
}
